import { Fragment, useEffect, useRef, useState } from 'react';

import axiosInstance from '../utils/axiosConfig';
import useTranslation from '../utils/useTranslation';

const ROTATE_MS = 7000;
const FALLBACK_BG = `${process.env.PUBLIC_URL}/home_bg.png`;

const STATS = [
  { valueKey: 'slider_stat_members_value', labelKey: 'slider_stat_members_label', icon: 'members' },
  { valueKey: 'slider_stat_chambers_value', labelKey: 'slider_stat_chambers_label', icon: 'building' },
  { valueKey: 'slider_stat_year_value', labelKey: 'slider_stat_year_label', icon: 'calendar' },
  { valueKey: 'slider_stat_service_value', labelKey: 'slider_stat_service_label', icon: 'handshake' },
];

function StatIcon({ type }) {
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  const icons = {
    members: (
      <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
        <rect x="3.5" y="11" width="7.5" height="9.5" rx="1" />
        <rect x="13" y="6" width="7.5" height="14.5" rx="1" />
        <rect x="5.25" y="13" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="7.75" y="13" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="5.25" y="16" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="7.75" y="16" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="14.75" y="8.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="17.25" y="8.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="14.75" y="11.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="17.25" y="11.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="14.75" y="14.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="17.25" y="14.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="14.75" y="17.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
        <rect x="17.25" y="17.5" width="1.4" height="1.4" fill="currentColor" stroke="none" />
      </svg>
    ),
    calendar: (
      <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
        <rect x="4" y="5.5" width="16" height="15.5" rx="2" />
        <path d="M8 3.5v4M16 3.5v4" />
        <path d="M4 10.5h16" />
        <circle cx="8" cy="13.25" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="12" cy="13.25" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="16" cy="13.25" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="8" cy="15.75" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="12" cy="15.75" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="16" cy="15.75" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="8" cy="18.25" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="12" cy="18.25" r="0.85" fill="currentColor" stroke="none" />
        <circle cx="16" cy="18.25" r="0.85" fill="currentColor" stroke="none" />
      </svg>
    ),
    handshake: (
      <svg viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
        <path d="m11 17 2 2a1 1 0 1 0 3-3" />
        <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
        <path d="m21 3 1 11h-2" />
        <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
        <path d="M3 4h8" />
      </svg>
    ),
  };
  return icons[type] || null;
}

export default function AdSlider() {
  const { t, lang } = useTranslation();
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('public/advertisements/', { params: { lang } })
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.advertisements || [];
        setAds(items);
        setIndex(0);
      })
      .catch(() => {
        if (!cancelled) setAds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const slides = ads.length
    ? ads
    : [{ id: 'fallback', image_url: FALLBACK_BG, link: null }];

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [slides.length]);

  const go = (i) => setIndex((i + slides.length) % slides.length);

  return (
    <section className="slider slider--hero" aria-roledescription="carousel">
      <div className="slider-track">
        {slides.map((ad, i) => {
          const img = <img src={ad.image_url} alt="" loading={i === 0 ? 'eager' : 'lazy'} />;
          return (
            <div
              key={ad.id}
              className={`slide${i === index ? ' is-active' : ''}`}
              aria-hidden={i === index ? undefined : 'true'}
            >
              <span
                className="slide-bg"
                style={{ backgroundImage: `url(${ad.image_url})` }}
                aria-hidden="true"
              />
              {ad.link ? (
                <a href={ad.link} target="_blank" rel="noreferrer" className="slide-link">
                  {img}
                </a>
              ) : (
                img
              )}
            </div>
          );
        })}

        {slides.length > 1 && (
          <>
            {index > 0 && (
              <button type="button" className="slider-btn prev" onClick={() => go(index - 1)} aria-label="Previous">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            )}
            {index < slides.length - 1 && (
              <button type="button" className="slider-btn next" onClick={() => go(index + 1)} aria-label="Next">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            )}
          </>
        )}
      </div>

      <div className="slider-bottom">
        <div className="slider-stats-anchor">
          {slides.length > 1 && (
            <div className="slider-dots slider-dots--hero">
              {slides.map((ad, i) => (
                <button
                  key={ad.id}
                  type="button"
                  className={`slider-dot${i === index ? ' is-active' : ''}`}
                  onClick={() => go(i)}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}

          <div className="slider-stats">
            {STATS.map((stat, index) => (
              <Fragment key={stat.valueKey}>
                {index > 0 && <span className="slider-stat-divider" aria-hidden="true" />}
                <div className="slider-stat">
                  <span className="slider-stat-icon">
                    <StatIcon type={stat.icon} />
                  </span>
                  <div className="slider-stat-body">
                    <span className="slider-stat-value">{t(stat.valueKey)}</span>
                    <span className="slider-stat-label">{t(stat.labelKey)}</span>
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
